import { useEffect, useRef, useState } from 'react';
import { resolveSrc } from '../engine/assetCache';
import { useSceneTime } from '../engine/sceneTime';

// Presentation-safe video: muted unless allowed, no controls, poster fallback,
// plays/pauses with the master clock, reads from the blob cache when available.
export function Vid({ video, className = '', style, active = true, loop = true, fit = 'cover', syncTime = null, restart = false, silent = false, volume = 1, ...rest }) {
  const ref = useRef(null);
  const wasActive = useRef(false);
  const [failed, setFailed] = useState(false);
  const { playing, audioOn } = useSceneTime();
  const src = video ? resolveSrc(video.src) : '';
  const allowAudio = !!(video?.audio && audioOn && !silent);

  useEffect(() => { setFailed(false); }, [src]);

  // Volume envelope (fade in/out) for clips that carry sound.
  useEffect(() => {
    const el = ref.current;
    if (el) el.volume = Math.max(0, Math.min(1, volume));
  }, [volume]);

  // Optional hard sync to the scene clock (used where overlays must line up with specific frames).
  useEffect(() => {
    const el = ref.current;
    if (!el || failed || syncTime == null) return;
    const d = el.duration;
    const target = Number.isFinite(d) && d > 0 ? syncTime % d : syncTime;
    if (Math.abs(el.currentTime - target) > 0.25) el.currentTime = target;
  }, [syncTime, failed]);

  useEffect(() => {
    const el = ref.current;
    if (!el || failed) return;
    if (active && playing) {
      if (restart && !wasActive.current) el.currentTime = 0;
      const p = el.play();
      // Autoplay with sound can be blocked before any user gesture — fall back to muted rather than freezing.
      if (p && p.catch) p.catch(() => { if (!el.muted) { el.muted = true; el.play().catch(() => {}); } });
    } else {
      el.pause();
    }
    wasActive.current = active;
  }, [active, playing, failed, src, restart]);

  if (!video) return null;
  if (failed) {
    return <img src={video.poster} alt="" className={`pk-vid pk-vid-fallback ${className}`} style={{ objectFit: fit, ...style }} />;
  }
  return (
    <video
      ref={ref}
      className={`pk-vid ${className}`}
      style={{ objectFit: fit, ...style }}
      src={src}
      poster={video.poster}
      muted={!allowAudio}
      loop={loop}
      playsInline
      preload="auto"
      disablePictureInPicture
      onError={() => setFailed(true)}
      {...rest}
    />
  );
}

// Screen is exactly 9:16 (inset 11px bezel) so portrait reels fill it edge-to-edge without cropping.
export function PhoneFrame({ children, width = 300, className = '', style, label, glow = false }) {
  const height = Math.round((width - 22) * (16 / 9) + 22);
  return (
    <div className={`pk-phone ${glow ? 'pk-phone-glow' : ''} ${className}`} style={{ width, height, ...style }}>
      <div className="pk-phone-screen">{children}</div>
      <div className="pk-phone-island" />
      {label && <div className="pk-phone-label">{label}</div>}
    </div>
  );
}

export function Glass({ children, className = '', style, active = false, ...rest }) {
  return (
    <div className={`pk-glass ${active ? 'pk-glass-active' : ''} ${className}`} style={style} {...rest}>{children}</div>
  );
}
